import { useState, useCallback } from "react";
import {
  defaultM1Month,
  readKeywordFile,
  type KeywordVolumeRow,
} from "@/lib/keyword-volume-parser";
import { uploadBatch } from "@/lib/keyword-volume-batch";
import { detectM1Months, type M1Input, type M1Method } from "@/lib/keyword-volume-m1";
import { keywordVolumeService } from "@/services/KeywordVolumeService";
import type { ImportStatus } from "@/hooks/useSpyImport";

/** One selected file: read and waiting for its M1 month, or unreadable. */
export interface PendingKeywordFile {
  fileName: string;
  rows: KeywordVolumeRow[];
  suggestedM1: string;
  /** How M1 was found: stored data, another file of the batch, or the export date. */
  method?: M1Method;
  detail?: string;
  /** Another pair of files disagrees with this month. */
  conflict?: string;
  /** Why the file cannot be imported; such files are skipped. */
  error?: string;
}

export const useKeywordVolumeImport = () => {
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [pending, setPending] = useState<PendingKeywordFile[] | null>(null);
  const [status, setStatus] = useState<ImportStatus>({ type: "idle", message: "" });
  // Bumped whenever files landed - also after a batch that stopped half-way.
  const [uploadedBatches, setUploadedBatches] = useState(0);

  const handleFileSelect = useCallback(
    async (
      e: React.ChangeEvent<HTMLInputElement>,
      fileInputRef?: React.RefObject<HTMLInputElement | null>,
    ) => {
      const files = Array.from(e.target.files ?? []);
      if (!files.length) return;
      setLoading(true);
      setProgress(0);
      const read: (M1Input | { fileName: string; error: string })[] = [];
      // One at a time: a file is a few MB of rows, eighteen of them at once is not.
      for (const [i, file] of files.entries()) {
        setStatus({ type: "idle", message: `Reading ${file.name} (${i + 1} / ${files.length})...` });
        try {
          const { rows, exportedAt } = await readKeywordFile(file);
          if (!rows.length) throw new Error("no keyword rows");
          const stored = await keywordVolumeService.matchStoredM1(rows, defaultM1Month(exportedAt));
          read.push({ fileName: file.name, rows, exportedAt, stored: stored ?? undefined });
        } catch (error) {
          read.push({ fileName: file.name, error: error instanceof Error ? error.message : String(error) });
        }
      }

      setStatus({ type: "idle", message: "Working out M1 for each file..." });
      const ok = read.filter((f): f is M1Input => !("error" in f));
      const months = detectM1Months(ok);
      const byName = new Map(ok.map((f, i) => [f, months[i]] as const));
      const pendingFiles: PendingKeywordFile[] = read.map((f) => {
        if ("error" in f) return { fileName: f.fileName, rows: [], suggestedM1: "", error: f.error };
        const m = byName.get(f)!;
        return {
          fileName: f.fileName, rows: f.rows, suggestedM1: m.m1Month,
          method: m.method, detail: m.detail, conflict: m.conflict,
        };
      });
      setPending(pendingFiles);
      const guessed = pendingFiles.filter((f) => f.conflict).length;
      setStatus({
        type: ok.length ? "idle" : "error",
        message: ok.length
          ? `${ok.length} of ${read.length} files read, M1 found automatically` +
            (guessed ? ` - ${guessed} with conflicting evidence, check those.` : ".")
          : "None of the files could be read.",
      });
      setLoading(false);
      if (fileInputRef?.current) fileInputRef.current.value = "";
    },
    [],
  );

  /** months: the confirmed M1 per pending file, same order; files with an error are skipped. */
  const confirmImport = useCallback(
    async (months: string[]) => {
      if (!pending) return;
      const files = pending
        .map((f, i) => ({ fileName: f.fileName, rows: f.rows, m1Month: months[i], error: f.error }))
        .filter((f) => !f.error && f.m1Month);
      setLoading(true);
      try {
        const result = await uploadBatch(
          files,
          {
            upload: (name, m1, rows, onProgress) =>
              keywordVolumeService.upload("file", name, m1, rows, (done) => onProgress(done)),
            finish: (id) => keywordVolumeService.finishUpload(id),
          },
          (done, total, i) => {
            setProgress(Math.round((done / total) * 100));
            setStatus({
              type: "idle",
              message: `Uploading ${files[i].fileName} (${i + 1} / ${files.length}): ${done} / ${total} rows...`,
            });
          },
        );
        if (result.uploaded.length) setUploadedBatches((n) => n + 1);
        if (result.failed) {
          setStatus({
            type: "error",
            message: `Uploaded ${result.uploaded.length} of ${files.length} files. Stopped at ${result.failed.fileName}: ${result.failed.error}`,
          });
          setPending((prev) => prev?.filter((f) => !result.uploaded.includes(f.fileName)) ?? null);
        } else {
          const rows = files.reduce((n, f) => n + f.rows.length, 0);
          setStatus({ type: "success", message: `Uploaded ${files.length} files, ${rows} rows.` });
          setPending(null);
        }
      } catch (error) {
        console.error("Keyword volume import error:", error);
        setStatus({
          type: "error",
          message: `Error: ${error instanceof Error ? error.message : "Unknown import error"}`,
        });
      } finally {
        setLoading(false);
      }
    },
    [pending],
  );

  return {
    loading,
    progress,
    status,
    pending,
    uploadedBatches,
    cancelImport: () => setPending(null),
    confirmImport,
    handleFileSelect,
  };
};
